import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { PayloadAction } from "@reduxjs/toolkit";

import axios from "../../../utils/api-client";
import { DEFAULT_REQUESTS_WINDOW_DAYS } from "../utils/overview-params";
import {
	SupervisorHomeState,
	SupervisorOverviewParams,
	SupervisorOverviewResponse,
} from "./supervisor-home-types";

export const fetchSupervisorOverview = createAsyncThunk<
	SupervisorOverviewResponse,
	SupervisorOverviewParams,
	{ rejectValue: string }
>(
	"supervisorHome/fetchSupervisorOverview",
	async (params, { rejectWithValue }) => {
		try {
			const res = await axios.get<SupervisorOverviewResponse>(
				`${process.env.REACT_APP_API_END_POINT}/companies/supervisors/overview`,
				{ params }
			);
			return res.data;
		} catch (error: any) { // eslint-disable-line
			return rejectWithValue(
				error?.response?.data?.message ?? "An error occurred"
			);
		}
	}
);

const initialState: SupervisorHomeState = {
	overview: null,
	fetchOverview: {
		status: "idle",
		errorMessage: "",
		fetchedAt: null,
	},
	requestsWindowDays: DEFAULT_REQUESTS_WINDOW_DAYS,
};

export const SupervisorHomeSlice = createSlice({
	name: "supervisorHome",
	initialState,
	reducers: {
		setRequestsWindowDays: (state, action: PayloadAction<number>) => {
			state.requestsWindowDays = action.payload;
		},
		resetSupervisorHome: () => initialState,
	},
	extraReducers: (builder) => {
		builder.addCase(fetchSupervisorOverview.pending, (state) => {
			state.fetchOverview.status = "pending";
			state.fetchOverview.errorMessage = "";
		});
		builder.addCase(fetchSupervisorOverview.fulfilled, (state, action) => {
			state.overview = action.payload;
			state.fetchOverview.status = "fulfilled";
			state.fetchOverview.fetchedAt = new Date().toISOString();
		});
		builder.addCase(fetchSupervisorOverview.rejected, (state, action) => {
			// Drop any previous snapshot — a stale one shown next to an error
			// reads as current data.
			state.overview = null;
			state.fetchOverview.status = "error";
			state.fetchOverview.errorMessage = action.payload ?? "An error occurred";
		});
	},
});

export const { setRequestsWindowDays, resetSupervisorHome } =
	SupervisorHomeSlice.actions;

export default SupervisorHomeSlice.reducer;
